/**
 * Admin panel shows content editors only — hide routes, IDs, and technical config.
 */

const SKIP_KEYS = new Set([
  'type',
  'path',
  'menuKey',
  'id',
  'key',
  'queryParam',
  'urlParam',
  'tokenKey',
  'layout',
  'is_show',
  'optionsSource',
  'dependsOn',
  'required',
  'accept',
  'multiple',
  'ref',
  'planId',
  'value',
  'icon',
  'filterKey',
  'parentSource',
  'waitMessageKey',
  'field',
  'step',
  'status',
  'matchLevel',
  'legacyId',
  'currency',
  'rows',
  'min',
  'max',
  'rate',
  'months',
  'tenureMonths',
  'returnDays',
  'supportDays',
  'engineGearbox',
  'returnEligible',
  'isCertified',
  'overallScore',
  'totalPoints',
  'passedPoints',
  'score',
  'total',
  'carId',
  'sampleCount',
  'priceVariance',
  'depreciationPerYear',
  'kmPenaltyPer1000',
  'tireWearFactor',
  'defaultBasePrice',
  'minPriceFloor',
  'defaultCarPrice',
  'defaultDownPaymentPercent',
  'defaultInterestRate',
  'defaultTenureMonths',
  'minCarPrice',
  'maxCarPrice',
  'minDownPaymentPercent',
  'maxDownPaymentPercent',
  'minInterestRate',
  'maxInterestRate',
  'minTenureMonths',
  'maxTenureMonths',
  'minLoanAmount',
  'maxLoanAmount',
  'tenureStep',
  'minSampleSize',
  'highConfidenceSample',
  'mediumConfidenceSample',
  'maxCars',
  'carPrice',
  'reportPdf',
  'photos',
  'categories',
  'history',
  'lastInspectedAt',
  'is_modify',
  'is_project',
  'home_show',
  'testDriveEnabled',
  'role',
  'duration',
  'perMonthSuffix',
  'currencySymbol',
]);

const SKIP_SUFFIX_RE = /(Path|Url|Param|Months|Percent|Rate)$/i;

const SKIP_BRANCH_RE = [
  /^engine(\.|$)/,
  /^calculator(\.|$)/,
  /\.engine(\.|$)/,
  /\.calculator(\.|$)/,
  /^validation(\.|$)/,
  /\.validation(\.|$)/,
  /^scoreTiers(\.|$)/,
  /^scoreRing(\.|$)/,
  /^tenureOptions(\.|$)/,
  /^partnerPlans(\.|$)/,
  /^breakdown(\.|$)/,
  /^carDetailLink(\.|$)/,
  /^order\.lifecycle(\.|$)/,
  /^tabs(\.|$)/,
  /\.options(\.|$)/,
  /^socials(\.|$)/,
  /^map(\.|$)/,
  /^heroCta(\.|$)/,
  /^inspection(\.|$)/,
];

const friendlyLabels = [
  [/^navItems\.(\d+)\.label$/, (m) => `Menu Item ${Number(m[1]) + 1}`],
  [/^plans\.(\d+)\.name$/, (m) => `Plan ${Number(m[1]) + 1} — Name`],
  [/^plans\.(\d+)\.badge$/, (m) => `Plan ${Number(m[1]) + 1} — Badge`],
  [/^plans\.(\d+)\.priceNote$/, (m) => `Plan ${Number(m[1]) + 1} — Price Note`],
  [/^plans\.(\d+)\.highlights$/, (m) => `Plan ${Number(m[1]) + 1} — Highlights`],
  [/^form\.fields\.(\d+)\.label$/, (m) => `Form Field ${Number(m[1]) + 1} — Label`],
  [/^form\.fields\.(\d+)\.placeholder$/, (m) => `Form Field ${Number(m[1]) + 1} — Placeholder`],
  [/^form\.fields\.(\d+)\.hint$/, (m) => `Form Field ${Number(m[1]) + 1} — Hint`],
  [/^fields\.(\d+)\.label$/, (m) => `Form Field ${Number(m[1]) + 1} — Label`],
  [/^fields\.(\d+)\.placeholder$/, (m) => `Form Field ${Number(m[1]) + 1} — Placeholder`],
  [/^carousel\.(\d+)\.text$/, (m) => `Carousel Slide ${Number(m[1]) + 1} — Text`],
  [/^heroType\.(\d+)\.name$/, (m) => `Hero Button ${Number(m[1]) + 1}`],
  [/^highlights\.(\d+)\.title$/, (m) => `Highlight ${Number(m[1]) + 1} — Title`],
  [/^highlights\.(\d+)\.description$/, (m) => `Highlight ${Number(m[1]) + 1} — Description`],
  [/^highlights\.(\d+)\.icon$/, (m) => `Highlight ${Number(m[1]) + 1} — Icon`],
  [/^sections\.(\d+)\.title$/, (m) => `Home Section ${Number(m[1]) + 1} — Title`],
  [/^sections\.(\d+)\.highlight$/, (m) => `Home Section ${Number(m[1]) + 1} — Highlight Text`],
  [/^sections\.(\d+)\.show$/, (m) => `Home Section ${Number(m[1]) + 1} — Visible on Homepage`],
  [/^carousel\.(\d+)\.image$/, (m) => `Carousel Slide ${Number(m[1]) + 1} — Image URL`],
  [/^pages\.login\.title$/, () => 'Login Page — Title'],
  [/^pages\.login\.subtitle$/, () => 'Login Page — Subtitle'],
  [/^pages\.signup\.title$/, () => 'Signup Page — Title'],
  [/^pages\.signup\.subtitle$/, () => 'Signup Page — Subtitle'],
];

export const formatContentLabel = (path) => {
  for (const [pattern, builder] of friendlyLabels) {
    const match = path.match(pattern);
    if (match) return builder(match);
  }

  return String(path)
    .split(/[._]/)
    .filter((part) => part && !/^\d+$/.test(part))
    .map((part) => {
      if (part === 'intro') return 'Introduction';
      if (part === 'cta') return 'Call To Action';
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(' — ');
};

export const shouldShowContentField = (path) => {
  if (!path || path.startsWith('_section_')) return true;

  if (SKIP_BRANCH_RE.some((re) => re.test(path))) return false;

  const key = path.split('.').pop();
  if (SKIP_KEYS.has(key)) return false;
  if (key === 'name' && /(?:form\.)?fields\.\d+\.name$/.test(path)) return false;
  if (SKIP_SUFFIX_RE.test(key)) return false;

  const navMatch = path.match(/^navItems\.\d+\.(\w+)$/);
  if (navMatch && navMatch[1] !== 'label') return false;

  const formFieldMatch = path.match(/(?:form\.)?fields\.\d+\.(\w+)$/);
  if (formFieldMatch && !['label', 'placeholder', 'hint'].includes(formFieldMatch[1])) {
    return false;
  }

  const planMatch = path.match(/^plans\.\d+\.(\w+)$/);
  if (planMatch && !['name', 'badge', 'priceNote', 'highlights'].includes(planMatch[1])) {
    return false;
  }

  const policyStepMatch = path.match(/steps\.\d+\.(\w+)$/);
  if (policyStepMatch && !['title', 'description'].includes(policyStepMatch[1])) {
    return false;
  }

  if (/actions\./.test(path) && /Path$/i.test(key)) return false;
  if (/order\./.test(path) && /Path$/i.test(key)) return false;
  if (/(^|\.)path$/i.test(path)) return false;

  return true;
};

export const isContentValue = (path, value) => {
  if (!shouldShowContentField(path)) return false;
  return value !== null && value !== undefined;
};

export const filterContentFields = (fields) =>
  fields.filter((field) => {
    if (field.type === 'section') {
      const sectionPath = field.name.replace(/^_section_/, '');
      return shouldShowContentField(sectionPath);
    }
    return shouldShowContentField(field.name);
  });
